import { obtenerCodigoArea, obtenerNumero } from './format'
import {
  consigneePhoneFromAccount,
  splitPhoneCountryCode,
  AccountPhoneLike,
} from './shipmentImportUtils'
import { PHONE_COUNTRY_CODE } from './shipmentValidation'

/**
 * Teléfono de un destinatario favorito, repartido en los tres campos que muestra
 * el formulario: el país, la operadora venezolana (que fuera de Venezuela no
 * existe) y los dígitos.
 */
export interface FavoritePhoneParts {
  /** Prefijo internacional, siempre con `+` ("+58", "+57"). */
  countryCode: string
  /** Operadora/FIJO. Vacía cuando el país no es Venezuela. */
  operadora: string
  /** Lo que va en el campo "Teléfono". Con `+58` son los dígitos SIN operadora. */
  number: string
}

// ---------------------------------------------------------------------------
// Cómo viaja el país del teléfono de un favorito
//
// El SP `Account/InsertFavoriteDestination` NO usa `accountCountryID` para el
// teléfono (se verificó contra dev: mandar Colombia=50 deja el registro igual,
// devuelve countryID 236). Lo único que hace con el teléfono es CONCATENAR
// `accountPhoneCode + accountPhone` en un campo de texto:
//
//   accountPhoneCode "424"   + "1362303"    → "4241362303"      (lo de siempre)
//   accountPhoneCode "+57"   + "3001234567" → "+573001234567"   (sin espacio: inválido)
//   accountPhoneCode "+57 "  + "3001234567" → "+57 3001234567"  ← el que sirve
//
// El último es exactamente el formato que exige el validador del backend
// (`^\+\d{1,3} \d{9,15}$`, ver BACKEND_PHONE_RE) y el que `splitPhoneCountryCode`
// sabe volver a separar. Por eso el código de país viaja CON un espacio final:
// no es un capricho de formato, es el separador que el SP no pone.
//
// Venezuela queda EXACTAMENTE como estaba (la operadora pelada, sin `+58`): es
// como están guardados todos los favoritos existentes y cambiarlo rompería la
// lectura en el resto de las pantallas.
// ---------------------------------------------------------------------------

/** Valor de `accountPhoneCode` para el SP, según el país elegido. */
export const favoritePhoneCodeForSave = (
  countryCode: string | null | undefined,
  operadora: string | null | undefined
): string => {
  const dial = countryCode || PHONE_COUNTRY_CODE
  // "FIJO" no es un prefijo, es "no elegiste operadora móvil": se va y el número
  // queda con sus 10 dígitos (código de área + número), como ya hacía la pantalla.
  if (dial === PHONE_COUNTRY_CODE) return (operadora || '').replace('FIJO', '')
  return `${dial} `
}

/**
 * Teléfono tal como lo devuelve `GetAllFavDestination` → los tres campos del
 * formulario.
 *
 * Antes esto se hacía con `obtenerCodigoArea`/`obtenerNumero` a secas, que solo
 * conocen prefijos venezolanos: un "+57 3001234567" caía en `FIJO` con los 12
 * dígitos adentro y el favorito se abría roto (y se volvía a guardar roto).
 */
export const favoritePhoneFromStored = (
  stored: string | null | undefined
): FavoritePhoneParts => {
  const { countryCode, phone } = splitPhoneCountryCode(stored || '')
  return splitVenezuelanParts(countryCode, phone)
}

/**
 * Mismo reparto, pero partiendo del `listAccountPhone[0]` de una cuenta ya
 * existente (el buscador de destinatarios y los destinos favoritos/recientes),
 * donde el código de país viene en su propio campo.
 */
export const favoritePhoneFromAccount = (
  phone: AccountPhoneLike | null | undefined
): FavoritePhoneParts => {
  const { consigneePhone, consigneePhoneCountryCode } =
    consigneePhoneFromAccount(phone)
  return splitVenezuelanParts(consigneePhoneCountryCode, consigneePhone)
}

/**
 * Fuera de Venezuela el número son los dígitos crudos: no hay operadora que
 * separar (es un concepto del plan de numeración venezolano) y partirlo con
 * `obtenerCodigoArea` le comería los primeros 3 dígitos.
 */
const splitVenezuelanParts = (
  countryCode: string,
  digits: string
): FavoritePhoneParts => {
  if (countryCode !== PHONE_COUNTRY_CODE)
    return { countryCode, operadora: '', number: digits }

  return {
    countryCode: PHONE_COUNTRY_CODE,
    operadora: digits ? obtenerCodigoArea(digits) : '',
    number: digits ? obtenerNumero(digits) : '',
  }
}
